import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Trash2 } from "lucide-react";

export default function NameListManager({ title, endpoint, listEndpoint, testidPrefix, placeholder }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listEndpoint);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [listEndpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const value = name.trim();
    if (!value) return;
    setSubmitting(true);
    try {
      await api.post(endpoint, { name: value });
      toast.success("Added");
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div data-testid={`${testidPrefix}-manager`}>
      <div className="mb-6">
        <h2 className="font-heading font-bold text-xl text-espresso-900 tracking-tight">{title}</h2>
        <p className="text-taupe-600 text-sm mt-1">{items.length} entries</p>
      </div>

      <form onSubmit={submit} className="card-soft p-5 mb-6 flex gap-3" data-testid={`${testidPrefix}-add-form`}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder}
          className="rounded-xl h-11 bg-taupe-100 border-taupe-200 flex-1"
          data-testid={`${testidPrefix}-name-input`}
        />
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold h-11 px-5"
          data-testid={`${testidPrefix}-add-button`}
        >
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </form>

      <div className="card-soft overflow-hidden">
        <ul>
          {loading && <li className="py-6 text-center text-taupe-500 text-sm">Loading…</li>}
          {!loading && items.length === 0 && (
            <li className="py-8 text-center text-taupe-500 text-sm">No items yet.</li>
          )}
          {items.map((it) => (
            <li key={it.id}
              className="flex items-center justify-between px-6 py-4 border-b border-taupe-200 last:border-0 hover:bg-taupe-100/40 transition-colors"
              data-testid={`${testidPrefix}-row`}
            >
              <span className="text-sm font-medium text-espresso-900">{it.name}</span>
              <button
                onClick={() => remove(it.id)}
                className="p-2 rounded-lg text-terracotta-500 hover:bg-terracotta-100 transition-colors"
                data-testid={`${testidPrefix}-delete-${it.id}`}
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
